import { useState, memo } from 'react';
import { Eye, XCircle } from 'lucide-react';
import DistributionExerciseur from '../../../components/VueQuestion/DistributionExerciseur';
import RichTextBlockEleve from '../../../components/VueQuestion/RichTextBlockEleve';
import TrueFalseTableElement from '../../../components/VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TrueFalseTableElement';
import QCMRenderer from '../../../components/VueQuestion/renderers/QCMRenderer';
import RelierRenderer from '../../../components/VueQuestion/renderers/RelierRenderer';
import OrdonnerRenderer from '../../../components/VueQuestion/renderers/OrdonnerRenderer';
import ClasserRenderer from '../../../components/VueQuestion/renderers/ClasserRenderer';
import { getTableElementConfig, getQuestionTypeLabel } from '../../../utils/questionUtils';
import { CARD_BG } from './ResultatsExercice';

/**
 * Vue "Par question" — grille de pills + cards dépliées avec distribution.
 * Chaque question peut être dépliée pour voir l'énoncé et la répartition des réponses.
 */
const ResultatsExerciceByQuestion = memo(function ResultatsExerciceByQuestion({ statsParQuestion, setPreviewQuestion }) {
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [showRepondants, setShowRepondants] = useState({});

  return (
    <div className="space-y-4">
      {/* Header — mini-cards questions + tout voir/masquer */}
      <div className="space-y-2">
        <div className="flex gap-2 overflow-x-auto pb-3 pt-1 justify-center flex-wrap">
          {statsParQuestion.map((q) => {
            const isActive = expandedQuestions[q.question_ordre];
            const textColor = q.taux >= 70 ? '#0F6E56' : q.taux >= 40 ? '#854F0B' : '#A32D2D';
            return (
              <button
                key={q.question_ordre}
                onClick={() => setExpandedQuestions(prev => ({ ...prev, [q.question_ordre]: !prev[q.question_ordre] }))}
                className={`flex flex-col items-center px-3.5 rounded-2xl cursor-pointer transition-all duration-150 hover:border-gray-400 ${
                  isActive ? 'border-2 border-blue-900 shadow-md shadow-blue-900/15' : 'border border-gray-200/60 shadow-sm'
                }`}
                style={{ background: CARD_BG }}
              >
                <span className={`text-[13px] font-medium 
                  ${isActive ? 'text-blue-900' : 'text-gray-800'}`}>Q{q.question_ordre}</span>
                <span className="text-[11px]
                 font-medium" style={{ color: textColor }}>{q.taux}%</span>
              </button>
            );
          })}
        </div>
        {/* Boutons tout voir/masquer */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const allRepondants = statsParQuestion.every(q => showRepondants[q.question_ordre]);
              const next = {};
              statsParQuestion.forEach(q => { next[q.question_ordre] = !allRepondants; });
              setShowRepondants(next);
            }}
            className="text-[11px] font-medium text-indigo-400 hover:text-indigo-600 whitespace-nowrap transition"
          >
            {statsParQuestion.every(q => showRepondants[q.question_ordre]) ? 'Masquer élèves' : 'Voir élèves'}
          </button>
          <button
            onClick={() => {
              const allExpanded = statsParQuestion.every(q => expandedQuestions[q.question_ordre]);
              const next = {};
              statsParQuestion.forEach(q => { next[q.question_ordre] = !allExpanded; });
              setExpandedQuestions(next);
            }}
            className="text-[11px] font-medium text-blue-500 hover:text-blue-700 whitespace-nowrap transition"
          >
            {statsParQuestion.every(q => expandedQuestions[q.question_ordre]) ? 'Tout masquer' : 'Tout voir'}
          </button>
        </div>
      </div>

      {/* Cards dépliées */}
      <div className="space-y-3">
        {statsParQuestion.filter(q => expandedQuestions[q.question_ordre]).map((q) => {
          const tableConfig = getTableElementConfig({ bonne_reponse: q.bonneReponse, elements_additionnels: q.elements_additionnels });
          return (
            <div
              key={q.question_ordre}
              className="rounded-2xl border border-blue-100/60 shadow-md
               shadow-blue-900/50 overflow-hidden bg-white"
              style={{ animation: 'fadeInUp 0.3s ease-out' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 rounded-lg bg-indigo-100 text-indigo-700 text-xs font-bold">Q{q.question_ordre}</span>
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium border border-gray-200">
                    {getQuestionTypeLabel(q)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${q.taux >= 70 ? 'bg-emerald-100 text-emerald-700' : q.taux >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                    {q.taux}%
                  </span>
                  <span className="text-[10px] text-gray-400">{q.bonnes}/{q.total} correct{q.bonnes > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPreviewQuestion(q)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition"
                    title="Aperçu complet"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => setExpandedQuestions(prev => ({ ...prev, [q.question_ordre]: false }))}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                    title="Masquer"
                  >
                    <XCircle size={14} />
                  </button>
                </div>
              </div>

              {/* Énoncé + renderers — alignés */}
              <div className="px-4 pb-3 space-y-1">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  Énoncé — {getQuestionTypeLabel(q)}
                </p>
                <div className="pl-1 overflow-y-auto text-base font-semibold text-gray-800 [&_img]:max-h-[100px] [&_img]:max-w-full [&_img]:object-contain [&_svg]:max-w-full [&_svg]:max-h-[120px] [&_svg]:object-contain">
                  <RichTextBlockEleve texte={q.texte} elements={q.elements_additionnels} theme="light" />
                </div>
                {tableConfig && (
                  <div className="mt-1">
                    <TrueFalseTableElement config={tableConfig} isPreview compact />
                  </div>
                )}
                {/* Renderers — choix possibles de la question */}
                {(() => {
                  const c = q.contenu || {};
                  if (q.type === 'qcm' && c.choix?.length) return <QCMRenderer choix={c.choix} bonneReponse="" light />;
                  if (q.subType === 'relier' && c.pairs) return <RelierRenderer pairs={c.pairs} ciblesLeurres={c.cibles_leurres} light />;
                  if (q.subType === 'ordonner' && c.items) return <OrdonnerRenderer items={c.items} light />;
                  if (q.subType === 'classer' && c.categories) return <ClasserRenderer categories={c.categories} light />;
                  return null;
                })()}
              </div>

              {/* Zone distribution — fond distinct */}
              <div className="bg-slate-200/60 border-t border-slate-300 rounded-b-xl">
                <div className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-sky-800/30" />
                    <span className="text-[10px] font-bold text-sky-800 uppercase tracking-wider">
                      Répartition des réponses
                    </span>
                    <div className="flex-1 h-px bg-sky-800/30" />
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <DistributionExerciseur question={q} repondants={q.repondants} expandAll={showRepondants[q.question_ordre]} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Hint si rien déplié */}
      {statsParQuestion.every(q => !expandedQuestions[q.question_ordre]) && (
        <p className="text-sm text-gray-400 italic text-center py-6">Cliquer sur une question pour voir le détail</p>
      )}
    </div>
  );
});

export default ResultatsExerciceByQuestion;
